import type { AlertSettings, AlertThresholds, AlertTypeKey, ApiResponse } from '@evi/shared';
import { api } from './api';
import type { AlertPreview } from './alertsView';

export { PREVIEW_TYPES, PREVIEW_TYPE_LABELS, addressList, previewRows, type AlertPreview, type PreviewClass, type PreviewCountsView, type PreviewType } from './alertsView';

/**
 * Quiet alerts in Admin (Warren, 29 Oct 2026: "Make them quiet by default, configurable in Admin"): the Api's views of
 * Settings, Alerts (apps/api routes/settings/alerts.ts, Super Admin only), its 7-day preview, and the snooze on the
 * charger page and behind the link in every alert email (routes/chargers/alert-snooze.ts, routes/facilities/alert-snooze.ts).
 */
export interface AlertSettingsView {
  value: AlertSettings;
  defaults: AlertSettings;
  labels: Record<AlertTypeKey, string>;
  help: Record<AlertTypeKey, string>;
  type_keys: AlertTypeKey[];
  bounds: { thresholds: Record<keyof AlertThresholds, { min: number; max: number; step: number; label: string }>; ops_recipients_max: number; staging_allowlist_max: number };
  effective: {
    ops_recipients: string[];
    ops_recipients_source: 'settings' | 'env';
    infra: { offline_email: boolean; refused_starts_email: boolean; delivery_policy_open: boolean };
    production: boolean;
  };
}

export const fetchAlertSettings = () => api.get('settings/alerts', { retry: 0 }).json<ApiResponse<AlertSettingsView>>().then((r) => r.data);
export const saveAlertSettings = (value: AlertSettings) => api.put('settings/alerts', { json: value }).json<ApiResponse<AlertSettingsView>>().then((r) => r.data);
export const previewAlertSettings = (settings: AlertSettings | null) =>
  api.post('settings/alerts/preview', { json: settings ? { settings } : {}, timeout: 60_000 }).json<ApiResponse<AlertPreview>>().then((r) => r.data);

export interface ChargerSnoozeView {
  active: { until: string; duration: '24h' | '7d'; duration_label: string; via: 'alert_email_link' | 'admin'; set_at: string } | null;
  recent: Array<{ at: string; action: 'snoozed' | 'unsnoozed'; duration: string | null; until: string | null; via: string | null }>;
  can_snooze: boolean;
}
export const fetchChargerSnooze = (chargerId: string) =>
  api.get(`chargers/${chargerId}/alert-snooze`, { retry: 0 }).json<ApiResponse<ChargerSnoozeView>>().then((r) => r.data);
export const setChargerSnooze = (chargerId: string, action: '24h' | '7d' | 'clear') =>
  api.put(`chargers/${chargerId}/alert-snooze`, { json: { action } }).json<ApiResponse<ChargerSnoozeView>>().then((r) => r.data);

export interface SnoozeLinkView {
  charger_label: string;
  identity: string;
  location_name: string;
  duration: '24h' | '7d';
  duration_label: string;
  snoozed_until: string | null;
  /** withdrawn: EV Initiative turned this charger's alerts back on after the email was sent, so this link no longer snoozes. */
  state: 'open' | 'snoozed' | 'withdrawn';
}
export const fetchSnoozeLink = (token: string) => api.get(`alert-snooze/${encodeURIComponent(token)}`, { retry: 0 }).json<ApiResponse<SnoozeLinkView>>().then((r) => r.data);
export const pressSnoozeLink = (token: string) => api.post(`alert-snooze/${encodeURIComponent(token)}`).json<ApiResponse<SnoozeLinkView>>().then((r) => r.data);
